import { useState, useEffect } from "react";
import useIsMobile from "../../hooks/useIsMobile";
import { ColorCodes } from "../../constants/colorCodes";
import CloseIcon from "@mui/icons-material/Close";
import { anatomyConstants } from "../../constants/anatomyConstants";

const UploadSTLModal = ({
  files = [],
  progress = [],
  uploading,
  success,
  onClose,
  onUpload,
}) => {
  const isMobile = useIsMobile();
  const [localFiles, setLocalFiles] = useState(files);
  const [editedTitles, setEditedTitles] = useState([]);
  const [editedOrderNumbers, setEditedOrderNumbers] = useState([]);
  const [editedRevisionNumbers, setEditedRevisionNumbers] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [caseReady, setCaseReady] = useState(false);

  // initialize local state when files prop changes
  useEffect(() => {
    setLocalFiles(files);
    setEditedTitles(files.map((f) => f.name.replace(/\.stl$/i, "")));
    setEditedOrderNumbers(files.map(() => "0"));
    setEditedRevisionNumbers(files.map(() => "1"));
    setColorList(
      files.map((f) => {
        const name = f.name.replace(/\.stl$/i, "").toLowerCase();
        return ColorCodes[name] || "#d3d3d3";
      })
    );
  }, [files]);

  // close modal automatically if no files remain
  useEffect(() => {
    if (localFiles.length === 0) {
      onClose();
    }
  }, [localFiles, onClose]);

  const handleTitleChange = (index, value) => {
    setEditedTitles((ts) => ts.map((t, i) => (i === index ? value : t)));
    if (ColorCodes[value.toLowerCase()]) {
      setColorList((cs) =>
        cs.map((c, i) => (i === index ? ColorCodes[value.toLowerCase()] : c))
      );
    }
  };

  const handleOrderChange = (index, delta) => {
    setEditedOrderNumbers((os) =>
      os.map((v, i) =>
        i === index ? Math.max(0, parseInt(v, 10) + delta).toString() : v
      )
    );
  };

  const handleRevisionChange = (index, delta) => {
    setEditedRevisionNumbers((rs) =>
      rs.map((v, i) =>
        i === index ? Math.max(1, parseInt(v, 10) + delta).toString() : v
      )
    );
  };

  const handleColorChange = (index, value) => {
    setColorList((cs) => cs.map((c, i) => (i === index ? value : c)));
  };

  const handleRemoveFile = (index) => {
    setLocalFiles((fs) => fs.filter((_, i) => i !== index));
    setEditedTitles((ts) => ts.filter((_, i) => i !== index));
    setEditedOrderNumbers((os) => os.filter((_, i) => i !== index));
    setEditedRevisionNumbers((rs) => rs.filter((_, i) => i !== index));
    setColorList((cs) => cs.filter((_, i) => i !== index));
  };

  const handleUploadClick = () => {
    onUpload({
      files: localFiles,
      titles: editedTitles,
      orderNumbers: editedOrderNumbers,
      revisionNumbers: editedRevisionNumbers,
      colorList,
      caseReady,
    });
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      style={{
        "--main-color": anatomyConstants.mainColor,
        "--main-color-bold": anatomyConstants.mainColorBold,
      }}
    >
      <div
        className={`bg-white p-8 rounded shadow-lg overflow-y-auto ${
          isMobile ? "w-full max-w-sm mx-4" : "w-1/2"
        }`}
      >
        <h2 className="text-xl font-semibold mb-4 text-[var(--main-color)]">
          Upload STL Files
        </h2>

        <div className="max-h-[60vh] overflow-y-auto">
          <div className="flex mb-2 font-semibold text-gray-600">
            <div className="flex-grow">Title</div>
            <div className="w-16 text-right mr-2">Render Order</div>
            <div className="w-16 text-right mr-2">Revision</div>
            <div className="w-10 text-center">Color</div>
            <div className="w-10 text-center" />
          </div>

          {localFiles.map((file, i) => (
            <div key={i} className="mb-4">
              <div className="flex items-center mb-2">
                <button
                  onClick={() => handleRemoveFile(i)}
                  disabled={uploading}
                  className="text-red-500 hover:text-red-700 mr-2"
                  title="Remove file"
                >
                  <CloseIcon />
                </button>
                <p className="font-medium text-gray-700">
                  {file.name.replace(/\.stl$/i, "")}
                </p>
              </div>
              <div className="flex items-center space-x-4 mb-2">
                <input
                  type="text"
                  value={editedTitles[i] || ""}
                  onChange={(e) => handleTitleChange(i, e.target.value)}
                  disabled={uploading}
                  className="border border-gray-300 p-2 rounded flex-grow focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                />
                <div className="relative w-16">
                  <input
                    type="text"
                    readOnly
                    value={editedOrderNumbers[i] || "0"}
                    className="border border-gray-300 p-2 rounded text-center w-full focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                  />
                  <div className="absolute inset-y-0 right-0 flex flex-col">
                    <button
                      onClick={() => handleOrderChange(i, 1)}
                      disabled={uploading}
                      className="h-5 w-full text-xs rounded-t bg-gray-400 text-white hover:bg-gray-500"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleOrderChange(i, -1)}
                      disabled={uploading}
                      className="h-5 w-full text-xs rounded-b bg-gray-400 text-white hover:bg-gray-500"
                    >
                      ▼
                    </button>
                  </div>
                </div>
                <div className="relative w-16">
                  <input
                    type="text"
                    readOnly
                    value={editedRevisionNumbers[i] || "1"}
                    className="border border-gray-300 p-2 rounded text-center w-full focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                  />
                  <div className="absolute inset-y-0 right-0 flex flex-col">
                    <button
                      onClick={() => handleRevisionChange(i, 1)}
                      disabled={uploading}
                      className="h-5 w-full text-xs rounded-t bg-gray-400 text-white hover:bg-gray-500"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleRevisionChange(i, -1)}
                      disabled={uploading}
                      className="h-5 w-full text-xs rounded-b bg-gray-400 text-white hover:bg-gray-500"
                    >
                      ▼
                    </button>
                  </div>
                </div>
                <input
                  type="color"
                  value={colorList[i] || "#d3d3d3"}
                  onChange={(e) => handleColorChange(i, e.target.value)}
                  disabled={uploading}
                  className="w-10 h-10 p-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[var(--main-color)]"
                />
              </div>
              {progress[i] != null && (
                <div className="mt-2">
                  <p className="text-sm text-gray-600">
                    Upload progress: {progress[i].toFixed(2)}%
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-[var(--main-color)] h-2.5 rounded-full"
                      style={{ width: `${progress[i]}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-sm text-gray-600 mb-2">
          * Assign each structure a render-order higher than any inner
          structures.
        </p>

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={caseReady}
              onChange={(e) => setCaseReady(e.target.checked)}
              disabled={uploading}
              className="w-5 h-5"
            />
            <span className="text-gray-700">Set the case ready?</span>
          </label>
        </div>

        <div className="flex justify-end space-x-4">
          {!uploading && (
            <button
              onClick={onClose}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 focus:ring-2 focus:ring-gray-300"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleUploadClick}
            disabled={uploading || !localFiles.length}
            className="bg-[var(--main-color)] text-white px-4 py-2 rounded hover:bg-[var(--main-color-bold)] focus:ring-2 focus:ring-[var(--main-color)]"
          >
            Upload
          </button>
        </div>

        {uploading && success && (
          <div className="mt-4 text-green-500 text-center">
            Upload Successful!
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadSTLModal;
